import { ChevronUpIcon, ChevronDownIcon, ChevronNextIcon } from "@/theme/icons";
import { Button, Collapse, Box, Divider, Typography } from "@mui/material";
import React, { useState } from "react";
import { riskLevelRAGColors } from "../../VitalsCategory/utils/riskLevels";
import { MetricItem } from "../types";
import { useTranslation } from "react-i18next";
import { convertPxToRem } from "@/utils";

export default function CollapsableMetricsList({
  metrics,
  onMetricClick,
  expanded: controlledExpanded,
  onExpandedChange,
}: {
  metrics: MetricItem[];
  onMetricClick?: (metric: MetricItem) => void;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}) {
  const { t } = useTranslation();
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isControlled =
    controlledExpanded !== undefined && onExpandedChange !== undefined;
  const expanded = isControlled ? controlledExpanded : internalExpanded;
  const setExpanded = isControlled ? onExpandedChange : setInternalExpanded;

  return (
    <React.Fragment>
      <Button
        variant="text"
        onClick={() => setExpanded(!expanded)}
        endIcon={expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
        fullWidth
        sx={{
          color: "primary.900",
          fontFamily: '"Manrope", sans-serif',
          fontWeight: 700,
          fontSize: "16px",
          textTransform: "none",
          p: 0,
          pt: 1.5,
          pb: 1,
          mt: 2,
          justifyContent: "space-between",
          "&:focus": {
            outline: "none",
          },
        }}
      >
        {expanded
          ? t("CollapsableMetricsList.hideDetails.title")
          : t("CollapsableMetricsList.title")}
      </Button>

      <Collapse in={expanded}>
        <Box mt={3}>
          {metrics.map((metric, index) => (
            <Box key={metric.id} data-testid={`metric-container-${metric.id}`}>
              {index > 0 && <Divider sx={{ my: 1 }} />}
              <Box
                data-testid={`metric-row-${metric.id}`}
                onClick={() => metric.showChevron && onMetricClick?.(metric)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 1,
                  cursor: metric.showChevron ? "pointer" : "default",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontFamily: '"Manrope", sans-serif',
                      fontWeight: 500,
                      fontSize: convertPxToRem(16),
                      color: "neutral.900",
                    }}
                  >
                    {metric.title}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: '"Manrope", sans-serif',
                      fontWeight: 500,
                      fontSize: convertPxToRem(16),
                      color: "neutral.900",
                    }}
                  >
                    {metric.value}
                  </Typography>
                  <Box
                    data-testid={`metric-status-dot-${metric.id}`}
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: riskLevelRAGColors[metric.status],
                    }}
                  />
                  {metric.showChevron && (
                    <ChevronNextIcon
                      sx={{ fontSize: 24, color: "neutral.light" }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </React.Fragment>
  );
}
